import { Component, OnInit } from "@angular/core";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { IDashboardApiData, IDashboardApiResponse } from "@models/dashboard-api.model";
import { AppStateService } from "@services/app-state/app-state.service";
import { RequestService } from "@services/request/request.service";
import { Chart, ChartConfiguration, ChartData, ChartType } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { BaseChartDirective } from "ng2-charts";

// Register once at module load — before any chart is instantiated
Chart.register(ChartDataLabels);

/**
 * The dashboard is switched between live markets. Exactly one is shown at a
 * time: order values in different currencies cannot be added together.
 */
type DashboardMarket = string;

/** One period's reading for the selected market. */
interface PeriodRow {
  /** Sortable period key, e.g. "2026-09-14" or "2026-09". */
  dt: string;
  /** What the x-axis shows, e.g. "14 Sep" or "Sep 2026". */
  label: string;
  value: number;
}

/**
 * One order chart. Same design as the admin dashboard's order/sales charts
 * (a bar per period in the market's colour, its total printed above the bar,
 * one chart per row) so the two portals read the same.
 */
interface MarketChart {
  key: string;
  title: string;
  subtitle: string;
  /** Y-axis title. */
  axis: string;
  /** Formats values as money. */
  money: boolean;
  data: ChartData;
  options: ChartConfiguration["options"];
}

@Component({
  selector: "fh-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.css"],
  standalone: true,
  imports: [BaseChartDirective, RefreshButtonComponent],
})
export class DashboardComponent implements OnInit {
  // Chart type
  barChartType: ChartType = "bar";

  get markets(): string[] {
    return this.appStateService.liveMarketCodes();
  }
  /** The market every chart is drawn for; SG by default. */
  market: DashboardMarket = "SG";
  /** That market's currency, as the API reports it (from the country table). */
  currency = "SGD";
  /** Set when the selected market's figures could not be loaded. */
  loadError = "";

  /** Same per-country colours as the admin dashboard, so SG is the same blue. */
  private readonly MARKET_COLOURS: Record<DashboardMarket, string> = {
    SG: "#0c55b8",
    MY: "#26a69a",
    US: "#ef5350",
  };

  /** The swatch beside a market's checkbox, matching its bar colour. */
  marketSwatch(iso2: DashboardMarket): string {
    return this.MARKET_COLOURS[iso2] ?? "#8d6e63";
  }

  /** The four order charts, in the admin dashboard's order. */
  charts: MarketChart[] = [
    this.newChart(
      "dailyOrders",
      "Daily Orders",
      "Orders",
      false,
      "Your confirmed orders per day in the selected market, for its last 31 days with orders."
    ),
    this.newChart(
      "dailySales",
      "Daily Sales",
      "Sales (SGD)",
      true,
      "Your sales per day in the selected market over the same window."
    ),
    this.newChart(
      "monthlyOrders",
      "Monthly Orders",
      "Orders",
      false,
      "Your confirmed orders per month in the selected market, for its last 15 months with orders."
    ),
    this.newChart(
      "monthlySales",
      "Monthly Sales",
      "Sales (SGD)",
      true,
      "Your sales per month in the selected market over the same window."
    ),
  ];

  constructor(
    private request: RequestService,
    public appStateService: AppStateService
  ) {}

  ngOnInit() {
    // AuthGaurd already performs a live session check before this route activates.
    this.loadDashboardData();
  }

  // Bound wrapper so fh-refresh-button gets a stable, correctly-`this`-bound reference.
  refreshDashboard = () => this.loadDashboardData();

  /**
   * SG / US behave as one exclusive choice: ticking one unticks the other, and
   * the ticked one cannot be unticked (there is always exactly one market).
   */
  selectMarket(iso2: DashboardMarket, event: Event): void {
    const box = event.target as HTMLInputElement;
    if (iso2 === this.market) {
      box.checked = true;
      return;
    }
    this.market = iso2;
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    const requested = this.market;
    const url = `/api/v1/dashboard?iso2=${requested}`;
    // A slower reply for a market the vendor has since switched away from
    // must not overwrite the charts of the one now selected.
    this.request.get<IDashboardApiResponse>(url).subscribe({
      next: (res) => {
        if (requested !== this.market) return;
        if (res.result_code === 1) {
          this.loadError = "";
          this.applyCurrency(res.data.currency);
          this.processCharts(res.data);
        } else {
          this.clearCharts();
        }
      },
      error: () => {
        if (requested === this.market) this.clearCharts();
      },
    });
  }

  /**
   * A failed load empties the charts rather than leave the previous market's
   * figures under this one's name.
   */
  private clearCharts(): void {
    this.loadError = `Could not load the ${this.market} dashboard. Try refreshing.`;
    const country = this.appStateService
      .allCountries()
      .find((c) => c.iso2.toUpperCase() === this.market);
    this.applyCurrency(country?.currency || this.market);
    for (const chart of this.charts) chart.data = { labels: [], datasets: [] };
  }

  private newChart(
    key: string,
    title: string,
    axis: string,
    money: boolean,
    subtitle: string
  ): MarketChart {
    return {
      key,
      title,
      axis,
      money,
      subtitle,
      data: { labels: [], datasets: [] },
      options: this.chartOptions(axis, money),
    };
  }

  /** Re-labels every money axis with the selected market's currency. */
  private applyCurrency(currency: string | undefined): void {
    this.currency = currency || this.market;
    for (const chart of this.charts) {
      if (!chart.money) continue;
      chart.axis = `Sales (${this.currency})`;
      chart.options = this.chartOptions(chart.axis, chart.money);
    }
  }

  private format(value: number, money: boolean): string {
    return money
      ? `${this.currency} ${Math.round(value).toLocaleString()}`
      : value.toLocaleString();
  }

  /** The admin dashboard's stacked-country chart options, unchanged. */
  private chartOptions(axis: string, money: boolean): ChartConfiguration["options"] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      // Room for the total printed above the tallest bar.
      layout: { padding: { top: 26 } },
      plugins: {
        legend: { display: true, position: "top" as const },
        // Labels are configured on the dataset in rebuildChart.
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            footer: (items: any[]) =>
              `Total: ${this.format(
                items.reduce((sum, it) => sum + (it.parsed?.y ?? 0), 0),
                money
              )}`,
          },
        },
      } as any,
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          type: "linear" as const,
          beginAtZero: true,
          title: { display: true, text: axis },
        },
      },
    };
  }

  private rebuildChart(key: string, rows: PeriodRow[]): void {
    const chart = this.charts.find((c) => c.key === key) as MarketChart;
    // Period keys are "YYYY-MM-DD" / "YYYY-MM", so a plain sort is chronological.
    const sorted = [...rows].sort((a, b) => a.dt.localeCompare(b.dt));
    const money = chart.money;
    const colour = this.marketSwatch(this.market);

    // One market is one segment, so - as on the admin dashboard with a single
    // country ticked - only the total above the bar is drawn, not the same
    // number again inside it.
    const dataset = {
      type: "bar" as const,
      label: this.market,
      data: sorted.map((r) => r.value),
      backgroundColor: colour,
      borderColor: colour,
      borderWidth: 0,
      stack: chart.key,
      datalabels: {
        labels: {
          total: {
            // Explicit: without it this inherits the display:false set on the
            // plugin root, and the total silently never draws.
            display: true,
            anchor: "end" as const,
            align: "top" as const,
            offset: 2,
            color: "#37474f",
            font: { size: 10, weight: "bold" as const },
            formatter: (v: number) => (v > 0 ? this.format(v, money) : ""),
          },
        },
      },
    } as any;

    chart.data = { labels: sorted.map((r) => r.label), datasets: [dataset] };
  }

  private processCharts(data: IDashboardApiData): void {
    const daily = data.daily || [];
    const monthly = data.monthly || [];
    this.rebuildChart(
      "dailyOrders",
      daily.map((r) => ({ dt: r.dt, label: r.date, value: Number(r.orders) || 0 }))
    );
    this.rebuildChart(
      "dailySales",
      daily.map((r) => ({ dt: r.dt, label: r.date, value: Number(r.sales) || 0 }))
    );
    this.rebuildChart(
      "monthlyOrders",
      monthly.map((r) => ({ dt: r.dt, label: r.month, value: Number(r.orders) || 0 }))
    );
    this.rebuildChart(
      "monthlySales",
      monthly.map((r) => ({ dt: r.dt, label: r.month, value: Number(r.sales) || 0 }))
    );
  }
}
